// ============================================
// FILE: App.tsx
// ============================================
import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { QuizSetup } from './components/quiz/QuizSetup';
import { QuestionsGrid } from './components/quiz/QuestionsGrid';
import { QuestionView } from './components/quiz/QuestionView';
import DataService from './services/DataService';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

type View = 'setup' | 'grid' | 'q1' | 'q2';

const QuranQuiz: React.FC = () => {
  const [currentView, setCurrentView] = useState<View>('setup');
  const [studentData, setStudentData] = useState<any>(null);
  const [selectedQuestions, setSelectedQuestions] = useState<any[]>([]);
  const [readingMode, setReadingMode] = useState<string>('mushaf');

  const handleStartQuiz = (grade: string, reading_mode: string, questions: any[]) => {
    // Store student selection
    localStorage.setItem('grade', grade);
    localStorage.setItem('reading_mode', reading_mode);
    setReadingMode(reading_mode);
    
    // Get manhaj range for the selected grade and reading_mode
    const manhajRange = DataService.getManhajRange(Number(grade), reading_mode);
    
    if (!manhajRange) {
      console.error('No manhaj found for grade', grade, 'and reading_mode', reading_mode);
      return;
    }

    // Store the selected questions (2 random questions)
    setSelectedQuestions(questions);
    localStorage.setItem('selectedQuestions', JSON.stringify(questions));
    
    // Store all questions for the range (in case we need them later)
    const allQuestions = DataService.getQuestionsByVidRange(
      manhajRange.from,
      manhajRange.to
    );
    
    localStorage.setItem('allquestions', JSON.stringify(allQuestions));
    localStorage.setItem('takenquestions', JSON.stringify([]));
    localStorage.setItem('manhajStart', String(manhajRange.from));
    localStorage.setItem('manhajEnd', String(manhajRange.to));
    localStorage.setItem('manhajCategory', manhajRange.category);
    
    setCurrentView('grid');
  };

  const handleSelectQuestion = (num: 1 | 2) => {
    setCurrentView(num === 1 ? 'q1' : 'q2');
  };

  const handleNext = () => {
    setCurrentView(currentView === 'q1' ? 'q2' : 'grid');
  };

  const handleDone = () => {
    setCurrentView('grid');
  };

  const handlePrev = () => {
    setCurrentView('q1');
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-purple-50 p-4">
        <div className="max-w-full mx-auto">
          {currentView === 'setup' && (
            <QuizSetup onStartQuiz={handleStartQuiz} />
          )}

          {currentView === 'grid' && (
            <QuestionsGrid
              questions={selectedQuestions}
              onSelectQuestion={handleSelectQuestion}
              goToQuiz={() => setCurrentView('setup')}
            />
          )}

          {currentView === 'q1' && (
            <QuestionView
              questionNumber={1}
              question={selectedQuestions[0]}
              onNext={handleNext}
              onDone={handleDone}
              readingMode={readingMode}
            />
          )}

          {currentView === 'q2' && (
            <QuestionView
              questionNumber={2}
              question={selectedQuestions[1]}
              onNext={handleNext}
              onDone={handleDone}
              onPrev={handlePrev}
              showPrev
              readingMode={readingMode}
            />
          )}
        </div>
      </div>
    </QueryClientProvider>
  );
};

export default QuranQuiz;